import {css, cx as addStyles} from '@emotion/css';
import {Button, Modal, theme} from '@remoov/remoov-components';
import React, {HTMLAttributes} from 'react';

export type ItemDetailsModalProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => unknown;
  code: string | number;
  imgUrl: string;
  imgAlt: string;
  category: string | number;
  brand: string;
  original_price: string;
  notMyItem?: () => unknown;
  requestReturn?: null | (() => unknown);
};

export const ItemDetailsModal = ({
  isOpen,
  setIsOpen,
  code,
  imgUrl,
  imgAlt,
  category,
  brand,
  original_price,
  notMyItem,
  requestReturn,
}: ItemDetailsModalProps) => (
  <Modal title={`Item #${code} Details`} isOpen={isOpen} setModal={setIsOpen}>
    <Content>
      <LeftSide>
        <ImageBox>
          <img src={imgUrl} alt={imgAlt} />
        </ImageBox>
      </LeftSide>
      <RightSide>
        <LineInfoContainer>
          <Title>
            Category: <Value>{category}</Value>
          </Title>
        </LineInfoContainer>
        <LineInfoContainer className={css({marginTop: theme.spacings.small})}>
          <Title>
            Brand: <Value>{brand}</Value>
          </Title>
        </LineInfoContainer>
        <LineInfoContainer className={css({marginTop: theme.spacings.small})}>
          <Title>
            Original Price: <Value>{original_price}</Value>
          </Title>
        </LineInfoContainer>
      </RightSide>
    </Content>
    <ButtonsContainer>
      {notMyItem && (
        <Button buttonStyle='primary' onClick={notMyItem}>
          Not my item
        </Button>
      )}
      {requestReturn && (
        <Button buttonStyle='primary' onClick={requestReturn}>
          Request return
        </Button>
      )}
    </ButtonsContainer>
    <Button
      buttonStyle='secondary'
      minimal
      style={{width: '100%'}}
      onClick={value => setIsOpen(!value)}
    >
      Close
    </Button>
  </Modal>
);
const Content = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'grid',
      gridTemplateColumns: '45% 55%',
      [theme.mediaMaxWidth.small]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: theme.spacings.small,
      },
    })}
    {...args}
  />
);
const LeftSide = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({display: 'flex', alignItems: 'center', justifyContent: 'center'})}
    {...args}
  />
);
const RightSide = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
    })}
    {...args}
  />
);
const ImageBox = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      height: '100%',
      maxWidth: '280px',
      maxHeight: '280px',
      margin: `0 ${theme.spacings.large}`,
      img: {width: '100%', height: '100%', objectFit: 'cover'},
      [theme.mediaMaxWidth.small]: {margin: 0},
    })}
    {...args}
  />
);
const LineInfoContainer = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '4px',
      }),
      className,
    )}
    {...rest}
  />
);
const textBase = css({
  fontFamily: theme.font.family,
  fontSize: theme.font.sizes.small,
  textAlign: 'left',
});
const Title = (args: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={addStyles(
      textBase,
      css({color: theme.colors.darkBlue, fontWeight: theme.font.medium}),
    )}
    {...args}
  />
);
const Value = (args: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={addStyles(
      textBase,
      css({color: theme.colors.darkGray, fontWeight: theme.font.normal}),
    )}
    {...args}
  />
);
const ButtonsContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacings.small,
      margin: `${theme.spacings.medium} 0 ${theme.spacings.small}`,
    })}
    {...args}
  />
);
